import { cva } from "class-variance-authority";
import { Lock, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import Image from "@/routing/Image";
import { cn } from "@/lib/utils";
import { resolveGoalIllustration } from "./goalsConfig";
import type { GoalDefinition, GoalIllustrationSet } from "./goalsConfig";
import { goalScreenStrings, goalCardStrings } from "./strings";

/**
 * GoalCard — classic study-type picker card. Uses the same semantic Tailwind
 * tokens as the rest of the app (primary, muted, card, border) so light/dark
 * and white-label brand colors flow through applyThemeBrandTokens automatically.
 */

export type GoalCardState = "idle" | "selected" | "dimmed";

const goalCardVariants = cva(
  cn(
    "group relative isolate flex h-full w-full flex-col overflow-hidden rounded-xl text-left",
    "text-foreground",
    "transition-[border-color,box-shadow,transform,opacity] duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:cursor-default",
  ),
  {
    variants: {
      state: {
        idle: cn(
          "cursor-pointer border border-transparent shadow-sm",
          "hover:-translate-y-1 hover:border-border hover:shadow-md",
        ),
        selected: "cursor-pointer border-2 border-primary shadow-none",
        dimmed:
          "cursor-default border border-transparent opacity-50 pointer-events-none",
      },
      featured: {
        true: "bg-muted dark:bg-card",
        false: "bg-muted dark:bg-card",
      },
      size: {
        default: "min-h-[300px]",
        compact: "min-h-[184px]",
      },
    },
    defaultVariants: { state: "idle", featured: false, size: "default" },
  },
);

/** Card scale. `compact` is the Quick Study carousel size — same content, smaller. */
export type GoalCardSize = "default" | "compact";

interface GoalCardProps {
  goal: GoalDefinition;
  state?: GoalCardState;
  isSelectionDisabled?: boolean;
  onSelect?: (goalId: GoalDefinition["id"]) => void;
  className?: string;
  /** Which illustration set to render (`?art=art` for the painted suite). */
  illustrationSet?: GoalIllustrationSet;
  /** Visual scale — `compact` shrinks the band, padding, and type. */
  size?: GoalCardSize;
  /** When true, suppresses the ⚡ Quick badge even in compact size. */
  hideQuickBadge?: boolean;
  /** When true, hides the illustration band so the card is text-only and slimmer. */
  hideIllustration?: boolean;
  /** Full-study-only goal shown in Quick Study mode — inert, dimmed, Lock + "Coming soon". */
  locked?: boolean;
  /** Badge copy when locked. Defaults to "Coming soon". */
  lockedLabel?: string;
}

export function GoalCard({
  goal,
  state = "idle",
  isSelectionDisabled = false,
  onSelect,
  className,
  illustrationSet = "art",
  size = "default",
  hideQuickBadge = false,
  hideIllustration = false,
  locked = false,
  lockedLabel = goalScreenStrings.comingSoon,
}: GoalCardProps) {
  const { t } = useTranslation('app')
  // A locked (full-study-only) card always renders in the dimmed visual
  // state in Quick Study mode, regardless of the state passed by the parent.
  const effectiveState = locked ? "dimmed" : state;
  // Derived from effectiveState so a locked card never reports aria-pressed.
  const isSelected = effectiveState === "selected";
  const isCompact = size === "compact";
  const copy = goalCardStrings[goal.id]
  const illustration = resolveGoalIllustration(goal, illustrationSet);

  return (
    <button
      type="button"
      data-testid={`goal-card-${goal.id}`}
      data-state={effectiveState}
      aria-pressed={isSelected}
      disabled={isSelectionDisabled || locked}
      onClick={() => onSelect?.(goal.id)}
      className={cn(
        goalCardVariants({ state: effectiveState, featured: goal.featured ?? false, size }),
        className,
      )}
    >
      <div
        className={cn(
          "relative flex flex-1 flex-col gap-2.5 px-[17px] pt-[17px] pb-0",
          isCompact && "gap-1.5 px-3 pt-3",
          hideIllustration && "pb-[17px]",
          hideIllustration && isCompact && "pb-3",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <span
            data-testid={`goal-method-tag-${goal.id}`}
            className={cn(
              "inline-flex w-fit items-center rounded-full px-3 py-[3px] text-[12px] font-medium leading-[1.4]",
              isCompact && "px-2 py-[1px] text-[10px]",
              goal.featured
                ? "bg-primary text-primary-foreground ring-0"
                : "bg-white text-foreground ring-1 ring-border dark:bg-card",
            )}
          >
            {copy.methodTag}
          </span>
          {locked && (
            <span
              data-testid={`goal-locked-badge-${goal.id}`}
              className={cn(
                // Same surface treatment as the method tag opposite it, so the
                // pair reads as one row on the already-dimmed card.
                "inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-2 py-[3px] text-[12px] font-medium leading-[1.4] text-muted-foreground ring-1 ring-border dark:bg-card",
                isCompact && "px-1.5 py-[1px] text-[10px]",
              )}
            >
              <Lock className="h-3 w-3" aria-hidden />
              {lockedLabel}
            </span>
          )}
        </div>

        <div className="flex items-start gap-1.5">
          {isCompact && !hideQuickBadge && (
            <span
              data-testid={`goal-quick-badge-${goal.id}`}
              className="mt-px inline-flex shrink-0 items-center gap-0.5 text-[10px] font-medium leading-[1.4] text-primary"
            >
              <Zap className="h-3 w-3 fill-current" aria-hidden />
              {t('creation.quickBadge')}
            </span>
          )}
          <h3
            className={cn(
              "font-display text-[18px] leading-[1.15] text-foreground",
              isCompact && "text-[13px]",
            )}
          >
            {copy.title}
          </h3>
        </div>
        <p
          className={cn(
            "line-clamp-4 text-[14px] leading-[1.5] text-muted-foreground",
            isCompact && "text-[11px] leading-[1.45]",
          )}
        >
          {copy.subtitle}
        </p>
      </div>

      {/*
        Light mode — 3-layer technique:
        1. B&W base: grayscale + brightness/contrast pushes cream → white, multiply erases it
        2. Color restore: same image via mix-blend-color repaints only the saturated accent pixel
           using the B&W luminance (zero-saturation gray pixels are unaffected)
        3. Hue shift: brand primary div via mix-blend-hue swaps the accent hue to the brand color
           (pixels with no saturation are unaffected → illustration body stays B&W)
      */}
      {!hideIllustration && <div className={cn("relative mt-auto h-[160px] overflow-hidden", isCompact && "h-[100px]")}>
        {/* base layer — gives mix-blend-multiply an opaque surface to blend against within this stacking context */}
        <div className="absolute inset-0 bg-muted dark:bg-card" />
        {/* layer 1 — B&W, no background */}
        <Image
          src={illustration.light}
          alt=""
          fill
          className={cn(
            "dark:hidden grayscale brightness-110 contrast-150 mix-blend-multiply",
            illustration.fullBleed
              ? cn("object-cover object-bottom", illustration.fullBleedClassName)
              : "object-contain object-bottom",
          )}
          sizes="(max-width: 640px) 100vw, 25vw"
        />
        {/* layer 2 — restores the accent saturation using the B&W luminance */}
        <Image
          src={illustration.light}
          alt=""
          fill
          aria-hidden
          className={cn(
            "dark:hidden mix-blend-color brightness-110 contrast-150",
            illustration.fullBleed
              ? cn("object-cover object-bottom", illustration.fullBleedClassName)
              : "object-contain object-bottom",
          )}
          sizes="(max-width: 640px) 100vw, 25vw"
        />
        {/* layer 3 — swaps accent hue → brand primary; gray (S=0) pixels unaffected */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 dark:hidden mix-blend-hue"
          style={{ background: "hsl(var(--primary))" }}
        />

        {/* dark mode: original illustration as-is */}
        <Image
          src={illustration.dark}
          alt=""
          fill
          className={cn(
            "hidden dark:block",
            illustration.fullBleed
              ? cn("object-cover object-bottom", illustration.fullBleedClassName)
              : "object-contain object-bottom",
          )}
          sizes="(max-width: 640px) 100vw, 25vw"
        />
      </div>}
    </button>
  );
}
